/**
 * Banner shown when a page's table is narrowed by a drill-down link from
 * another page (see DRILLDOWN_PARAMS in Helper).
 *
 * A narrowed table looks exactly like an empty account, so this says plainly
 * that the full dataset was fetched and then filtered, what it was filtered to,
 * and how to get back to everything. When nothing matched it says that outright
 * rather than leaving the reader with a blank table.
 */
const DrilldownNote = ({
  entityName = 'rows',
  sourceEntityName = 'source',
  sourceLabel,
  sourceId,
  tabLabel,
  requestedCount = 0,
  shownCount = 0,
  totalCount = 0,
  loading = false,
  onClear,
}) => {
  const missingCount = Math.max(0, requestedCount - shownCount);
  const noMatches = !loading && shownCount === 0;
  const source = sourceLabel || sourceId;
  const sourceText = source
    ? `${sourceEntityName} ${source}${sourceLabel && sourceId && sourceLabel !== sourceId ? ` (${sourceId})` : ''}`
    : `the linked ${sourceEntityName}`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        flexWrap: 'wrap',
        padding: '10px 14px',
        marginBottom: '14px',
        borderRadius: '8px',
        border: '1px solid var(--line)',
        borderLeft: `3px solid ${noMatches ? 'var(--brand)' : 'var(--blue)'}`,
        background: noMatches ? 'var(--brand-soft)' : 'var(--blue-soft)',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: '260px' }}>
        <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '13px' }}>
          Filtered view · <span style={{ fontWeight: 600 }}>{sourceText}</span>
        </div>

        <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '3px', lineHeight: 1.55 }}>
          {loading ? (
            <span>
              Loading {tabLabel ? `${tabLabel} ` : ''}{entityName}, then narrowing to the {requestedCount} linked{' '}
              {entityName}&hellip;
            </span>
          ) : noMatches ? (
            <span style={{ color: 'var(--text)' }}>
              None of the {requestedCount} linked {entityName} appear in
              {tabLabel ? ` the ${tabLabel} data` : ' this tab'} ({totalCount} {entityName} loaded). They may sit under
              a different tab, or have no recommendation yet.
            </span>
          ) : (
            <span>
              All {totalCount} {tabLabel ? `${tabLabel} ` : ''}{entityName} were loaded; the table below shows only the{' '}
              <strong style={{ color: 'var(--text)' }}>{shownCount}</strong> linked to this {sourceEntityName}. KPIs and
              charts above still cover all {totalCount}.
              {missingCount > 0 && (
                <>
                  {' '}
                  <span style={{ color: 'var(--brand)', fontWeight: 600 }}>
                    {missingCount} of the {requestedCount} linked {entityName}{' '}
                    {missingCount === 1 ? 'is' : 'are'} not in this tab
                  </span>
                  {' '}&mdash; try another tab.
                </>
              )}
            </span>
          )}
        </div>
      </div>

      <button className="btn ghost" onClick={onClear} style={{ padding: '6px 12px', fontSize: '12px' }}>
        ✕ Show all {entityName}
      </button>
    </div>
  );
};

export default DrilldownNote;
